import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SignaturePad } from "@/components/ui/SignaturePad";
import { Check, X, Building2, Calendar, CheckCircle2, XCircle, PenLine } from "lucide-react";
import { isSectionHidden } from "@/lib/contract-sections";

export const Route = createFileRoute("/contracts/approve/$token")({
  component: PublicContractApprovalPage,
});

type Contract = {
  id: string;
  number: string;
  title: string;
  scope: string | null;
  terms: string | null;
  amount: number;
  currency: string;
  start_date: string | null;
  end_date: string | null;
  retainage_percent: number;
  hidden_sections: string[] | null;
  clients: { name: string; email: string | null } | null;
  companies: { name: string; logo_url: string | null; email: string | null } | null;
};

type TokenRow = {
  id: string;
  contract_id: string;
  status: "sent" | "approved" | "rejected" | "revoked";
  responded_at: string | null;
  response_notes: string | null;
  client_signature_name: string | null;
};

function PublicContractApprovalPage() {
  const { token } = Route.useParams();
  const { t } = useTranslation();
  const [tokenRow, setTokenRow] = useState<TokenRow | null>(null);
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signName, setSignName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: payload, error: rpcErr } = await supabase.rpc("get_contract_for_approval", {
        p_token: token,
      });
      if (cancelled) return;
      if (rpcErr || !payload) {
        setError(t("pages.contractApproval.invalidLink"));
        setLoading(false);
        return;
      }
      const tk = (payload as { token: TokenRow }).token;
      const c = (payload as { contract: Contract }).contract;
      setTokenRow(tk);
      setContract(c ?? null);
      if (c?.clients?.name) {
        setSignName(c.clients.name);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const respond = async (decision: "approved" | "rejected") => {
    if (!tokenRow) return;
    if (decision === "approved") {
      if (!signName.trim()) {
        toast.error(t("pages.contractApproval.signerNameRequired"));
        return;
      }
      if (!signature) {
        toast.error(t("pages.signature.signatureRequired"));
        return;
      }
    }
    setSubmitting(true);
    const respondedAt = new Date().toISOString();
    const trimmedNotes = notes.trim() || null;
    const signatureName = decision === "approved" ? signName.trim() : null;
    const { error: uErr } = await supabase.rpc("respond_to_contract_token", {
      _token: token,
      _decision: decision,
      _response_notes: trimmedNotes ?? "",
      _client_signature_name: signatureName ?? "",
      _client_signature_data: decision === "approved" ? (signature ?? "") : "",
    });
    setSubmitting(false);
    if (uErr) {
      toast.error(t("pages.contractApproval.submitFailed"), {
        description: uErr.message ?? t("pages.contractApproval.submitFailedDefault"),
      });
      return;
    }
    setTokenRow({
      ...tokenRow,
      status: decision,
      response_notes: trimmedNotes,
      client_signature_name: signatureName,
      responded_at: respondedAt,
    } as TokenRow);
    toast.success(
      decision === "approved"
        ? t("pages.contractApproval.approvedToast")
        : t("pages.contractApproval.rejectedToast"),
    );
  };

  const formatMoney = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: contract?.currency || "USD",
    }).format(Number(v) || 0);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !tokenRow || !contract) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-3 text-xl font-semibold">
            {t("pages.contractApproval.linkUnavailable")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error ?? t("pages.contractApproval.loadFailed")}
          </p>
        </div>
      </div>
    );
  }

  const alreadyResponded = tokenRow.status !== "sent";
  const wasApproved = tokenRow.status === "approved";
  const wasRejected = tokenRow.status === "rejected";
  const wasRevoked = tokenRow.status === "revoked";

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {contract.companies?.logo_url ? (
              <img
                src={contract.companies.logo_url}
                alt={contract.companies.name}
                className="h-10 w-10 rounded object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold">
                {contract.companies?.name ?? t("pages.contractApproval.contractFallback")}
              </p>
              {contract.companies?.email && (
                <p className="text-xs text-muted-foreground">{contract.companies.email}</p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="font-mono text-xs">
            {contract.number}
          </Badge>
        </div>

        {alreadyResponded && (
          <div
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              wasApproved
                ? "border-primary/30 bg-primary/5"
                : wasRejected
                  ? "border-destructive/30 bg-destructive/5"
                  : "border-border bg-muted"
            }`}
          >
            {wasApproved ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 text-destructive" />
            )}
            <div className="flex-1">
              <p className="font-medium">
                {wasApproved && t("pages.contractApproval.youApproved")}
                {wasRejected && t("pages.contractApproval.youDeclined")}
                {wasRevoked && t("pages.contractApproval.linkRevoked")}
              </p>
              {tokenRow.responded_at && (
                <p className="text-xs text-muted-foreground">
                  {new Date(tokenRow.responded_at).toLocaleString()}
                </p>
              )}
              {tokenRow.client_signature_name && (
                <p className="mt-1 font-serif text-lg italic">{tokenRow.client_signature_name}</p>
              )}
              {tokenRow.response_notes && (
                <p className="mt-2 text-sm whitespace-pre-wrap">{tokenRow.response_notes}</p>
              )}
            </div>
          </div>
        )}

        <div className="rounded-lg border border-border bg-card p-6">
          <h1 className="text-2xl font-semibold tracking-tight">{contract.title}</h1>
          {contract.clients?.name && (
            <p className="mt-1 text-sm text-muted-foreground">
              {t("pages.contractApproval.preparedFor", { name: contract.clients.name })}
            </p>
          )}

          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.contractApproval.start")}
                </p>
                <p className="text-sm font-medium">
                  {contract.start_date ? new Date(contract.start_date).toLocaleDateString() : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.contractApproval.end")}
                </p>
                <p className="text-sm font-medium">
                  {contract.end_date ? new Date(contract.end_date).toLocaleDateString() : "—"}
                </p>
              </div>
            </div>
            {!isSectionHidden(contract.hidden_sections, "payment") && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.contractApproval.amount")}
                </p>
                <p className="text-lg font-semibold">{formatMoney(contract.amount)}</p>
              </div>
            )}
          </div>

          {contract.scope && !isSectionHidden(contract.hidden_sections, "scope") && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                {t("pages.contractApproval.scope")}
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{contract.scope}</p>
            </div>
          )}

          {contract.terms && !isSectionHidden(contract.hidden_sections, "payment") && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                {t("pages.contractApproval.terms")}
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {contract.terms}
              </p>
            </div>
          )}
        </div>

        {!alreadyResponded && (
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-sm font-semibold">{t("pages.contractApproval.yourResponse")}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("pages.contractApproval.responseHint")}
            </p>

            <div className="mt-4 space-y-1.5">
              <Label htmlFor="signer-name" className="flex items-center gap-1.5">
                <PenLine className="h-3.5 w-3.5" />
                {t("pages.contractApproval.signerName")}
              </Label>
              <Input
                id="signer-name"
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                placeholder={t("pages.contractApproval.signerNamePlaceholder")}
              />
              <p className="text-xs text-muted-foreground">
                {t("pages.contractApproval.signerNameHint")}
              </p>
            </div>

            <div className="mt-4">
              <SignaturePad
                value={signature}
                onChange={setSignature}
                label={t("pages.signature.signatureLabel")}
                hint={t("pages.signature.signatureHint")}
                clearLabel={t("pages.signature.signatureClear")}
              />
            </div>

            <Textarea
              placeholder={t("pages.contractApproval.messagePlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-4"
              rows={3}
            />
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={() => respond("approved")}
                disabled={submitting}
                className="flex-1 gap-2"
              >
                <Check className="h-4 w-4" />
                {t("pages.contractApproval.approveBtn")}
              </Button>
              <Button
                onClick={() => respond("rejected")}
                disabled={submitting}
                variant="outline"
                className="flex-1 gap-2"
              >
                <X className="h-4 w-4" />
                {t("pages.contractApproval.declineBtn")}
              </Button>
            </div>
          </div>
        )}

        <p className="pt-2 text-center text-xs text-muted-foreground">
          {t("pages.contractApproval.poweredBy", {
            name: contract.companies?.name ?? "Business Dite Ads",
          })}
        </p>
      </div>
    </div>
  );
}
